import { Descriptions, Modal, Spin } from "antd";
import { customerApi } from "api/customer.api";
import { opportunityApi } from "api/opportunity.api";
import { AppButton } from "components/antd-ui";
import { CommentThread } from "components/Comment/CommentThread";
import { StatusCell } from "components/table";
import { ViewDetailButton } from "components/ViewDetailButton/ViewDetailButton";
import { observer } from "mobx-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CommentRefType } from "types/comment";
import { Customer } from "types/customer";
import { Opportunity } from "types/opportunity";
import { formatDateTime } from "utils/date";
import { can } from "utils/auth";
import { CustomerModal } from "views/Customer/components/CustomerModal";
import { OPPORTUNITY_PATH, QUOTE_PATH, opportunityStatusTrans, quoteStatusTrans } from "../opportunity.meta";
import { OpportunityFiles } from "./OpportunityFiles";
import { ProductTable, type ProductRow } from "./ProductTable";

type OpportunityDetailModalProps = {
  opportunityId?: number;
  open: boolean;
  onClose: () => void;
};

const noop = () => {};

const toRows = (opportunity?: Opportunity): ProductRow[] =>
  (opportunity?.opportunityDetails || []).map((detail) => ({
    key: `id-${detail.id}`,
    id: detail.id,
    description: detail.description || "",
    unit: detail.unit || "",
    quantity: detail.quantity ?? null,
    price: detail.price ?? null,
    vatPercent: detail.vatPercent ?? 0,
    partnerDiscount: detail.partnerDiscount ?? 0,
  }));

// Popup xem nhanh cơ hội / báo giá (chỉ đọc). Sửa thì mở trang đầy đủ.
export const OpportunityDetailModal = observer(({ opportunityId, open, onClose }: OpportunityDetailModalProps) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [opportunity, setOpportunity] = useState<Opportunity>();
  const customerModalRef = useRef<CustomerModal>(null);

  const isQuote = !!opportunity?.quoteStatus;
  const fullPagePath = isQuote ? QUOTE_PATH : OPPORTUNITY_PATH;
  const canOpenFullPage = !!opportunity && can(fullPagePath, "VIEW");
  const customerId = opportunity?.customer?.id;
  const canViewCustomer = can("/customer", "VIEW");

  useEffect(() => {
    if (!open || !opportunityId) {
      setOpportunity(undefined);
      return;
    }

    let ignore = false;
    const load = async () => {
      setLoading(true);
      setOpportunity(undefined);
      try {
        const res = await opportunityApi.findOne(opportunityId);
        if (!ignore) setOpportunity(res.data as Opportunity);
      } catch {
        if (!ignore) setOpportunity(undefined);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    load();

    return () => {
      ignore = true;
    };
  }, [open, opportunityId]);

  const handleViewCustomer = useCallback(async () => {
    if (!customerId) return;
    const res = await customerApi.findOne(customerId);
    customerModalRef.current?.handleView(res.data as Customer);
  }, [customerId]);

  const openFullPage = useCallback(() => {
    if (!opportunity) return;
    onClose();
    navigate(`${fullPagePath}/${opportunity.id}`);
  }, [fullPagePath, navigate, onClose, opportunity]);

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        title={isQuote ? "Chi tiết báo giá" : "Chi tiết cơ hội"}
        width={1200}
        style={{ top: 20 }}
        footer={
          <div className="flex justify-end gap-2">
            <AppButton onClick={onClose}>Đóng</AppButton>
            {canOpenFullPage && (
              <AppButton tone="primary" onClick={openFullPage}>
                Mở trang đầy đủ
              </AppButton>
            )}
          </div>
        }
      >
        <Spin spinning={loading}>
          {opportunity && (
            <div className="flex flex-col gap-6">
              <Descriptions column={2} size="small" labelStyle={{ color: "#667085", width: 150 }} title="Thông tin chung">
                <Descriptions.Item label="Tên cơ hội" span={2}>
                  <span className="font-semibold">{opportunity.name || "--"}</span>
                </Descriptions.Item>
                <Descriptions.Item label="Khách hàng">
                  <span className="inline-flex items-center gap-2">
                    {opportunity.customer?.name || "--"}
                    {opportunity.customer && canViewCustomer && (
                      <ViewDetailButton title="Xem thông tin chi tiết khách hàng" onClick={handleViewCustomer} />
                    )}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="Mã báo giá">{opportunity.code || "--"}</Descriptions.Item>
                <Descriptions.Item label="Trạng thái">
                  {opportunity.quoteStatus ? (
                    <StatusCell value={opportunity.quoteStatus as never} trans={quoteStatusTrans as never} />
                  ) : (
                    <StatusCell value={opportunity.status as never} trans={opportunityStatusTrans as never} />
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Người tạo">
                  {opportunity.createdStaff?.name || opportunity.createdStaff?.username || "--"}
                </Descriptions.Item>
                <Descriptions.Item label="Ngày tạo">
                  {opportunity.createdAt > 0 ? formatDateTime(opportunity.createdAt) : "--"}
                </Descriptions.Item>
                {opportunity.canViewPartner && (
                  <Descriptions.Item label="Đối tác giới thiệu">{opportunity.partnerName || "--"}</Descriptions.Item>
                )}
                {!!opportunity.failReason && (
                  <Descriptions.Item label="Lý do thất bại" span={2}>
                    <span style={{ whiteSpace: "pre-wrap" }}>{opportunity.failReason}</span>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Mô tả của khách" span={2}>
                  <span style={{ whiteSpace: "pre-wrap" }}>{opportunity.customerDescription || "--"}</span>
                </Descriptions.Item>
                <Descriptions.Item label="Ghi chú nội bộ" span={2}>
                  <span style={{ whiteSpace: "pre-wrap" }}>{opportunity.internalNote || "--"}</span>
                </Descriptions.Item>
                <Descriptions.Item label="Link đính kèm" span={2}>
                  {opportunity.links?.length ? (
                    <div className="flex flex-col gap-1">
                      {opportunity.links.map((link) => (
                        <a key={link} href={link} target="_blank" rel="noreferrer">
                          {link}
                        </a>
                      ))}
                    </div>
                  ) : (
                    "--"
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="File từ khách hàng">
                  {opportunity.customerFiles?.length ? (
                    <OpportunityFiles value={opportunity.customerFiles} readOnly onChange={noop} />
                  ) : (
                    "--"
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="File nội bộ">
                  {opportunity.internalFiles?.length ? (
                    <OpportunityFiles value={opportunity.internalFiles} readOnly onChange={noop} />
                  ) : (
                    "--"
                  )}
                </Descriptions.Item>
              </Descriptions>

              <div>
                <h3 className="font-semibold mb-2">Thông tin hàng hóa</h3>
                <ProductTable rows={toRows(opportunity)} readOnly showPartner={!!opportunity.canViewPartner} onChange={noop} />
              </div>

              <div>
                <h3 className="font-semibold mb-2">Bình luận</h3>
                <CommentThread refType={CommentRefType.Opportunity} refId={opportunity.id} code={opportunity.code} />
              </div>
            </div>
          )}
        </Spin>
      </Modal>
      <CustomerModal ref={customerModalRef} onSubmitOk={noop} onClose={noop} />
    </>
  );
});
